import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Icon from "Components/Icon/Icon";
import usePromptState from "hooks/usePromptState";

import getPlatform, { STORE_URLS } from "./platform";
import styles from "./AppDownloadBanner.module.scss";

const PROMPT_KEY = "app-download";
const MAX_APPEARANCES = 2;

const track = (event, payload) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
};

/**
 * Banner de descarga de la app nativa para quien entra al panel desde el
 * browser del celular o de una tablet (card 687, nodo de Figma 2778:8).
 *
 * El panel está diseñado para desktop: la idea es ofrecer la app justo en el
 * momento en que el usuario está sufriendo la versión degradada.
 *
 * Aparece dos veces y no más. Una aparición es **un montaje del componente**,
 * o sea una carga de página: navegar dentro del panel no cuenta, pero un F5 o
 * una pestaña nueva sí. Con el presupuesto en dos, de dónde cuelgue el
 * componente cambia por completo cuánto vive — ver el comentario en App.js.
 *
 * El contador sube en cada aparición, la ignore o la cierre con la X — cerrarla
 * no suma nada aparte, la aparición ya se contó. Si toca DESCARGAR no vuelve
 * nunca: ya cumplió el objetivo.
 */
const AppDownloadBanner = () => {
  const { t } = useTranslation();
  const { shown, clicked, markShown, markDismissed, markClicked } =
    usePromptState(PROMPT_KEY);
  const [platform] = useState(getPlatform);
  const [hidden, setHidden] = useState(false);

  // El tope se mide contra el contador al montar y no en vivo: markShown() lo
  // incrementa en este mismo mount, y leyéndolo en vivo el banner se borraría
  // apenas termina de aparecer. `clicked` sí va en vivo, para que descargar
  // desde otra pestaña lo baje también acá.
  const [shownBefore] = useState(() => shown);

  const visible =
    !!platform && !hidden && !clicked && shownBefore < MAX_APPEARANCES;

  // Número de esta aparición (1 o 2) — el `session_count` de banner_app_shown.
  const appearance = useRef(0);

  useEffect(() => {
    // Una sola vez por montaje: sin esta guarda, cada re-render del panel
    // sumaría una aparición y el denominador de conversión queda inservible.
    if (!visible || appearance.current) return;

    const state = markShown();
    // Sin poder registrar la aparición el tope de dos es inaplicable: el banner
    // volvería en cada carga, para siempre, y GA4 recibiría una impresión por
    // cada una. Se esconde y no se emite nada.
    if (!state) {
      setHidden(true);
      return;
    }

    appearance.current = state.shown;
    track("banner_app_shown", {
      platform,
      session_count: appearance.current,
    });
  }, [visible, platform, markShown]);

  if (!visible) return null;

  // `dismissed` no decide nada — el banner se apaga por apariciones, no por
  // cierres. Se persiste sólo porque el evento manda el acumulado, y para eso
  // tiene que sobrevivir a la carga de página.
  const onDismiss = () => {
    setHidden(true);
    const state = markDismissed();
    if (state) track("banner_app_dismissed", { dismiss_count: state.dismissed });
  };

  const onDownload = () => {
    markClicked();
    track("banner_app_cta_click", { platform });
  };

  return (
    <div className={styles.banner} data-testid="app-download-banner">
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <img className={styles.logo} src="/favicon.png" alt="" />
          <span className={styles.title}>
            {t("app_banner_title", "Tu experiencia es mejor en la App")}
          </span>
        </div>
        <button
          type="button"
          className={styles.close}
          onClick={onDismiss}
          aria-label={t("app_banner_close", "Cerrar")}
        >
          <Icon name="close_small" size={18} />
        </button>
      </div>

      {/* `_blank` no es cosmético: navegando en la misma pestaña el browser
          puede descargar la página antes de que GTM mande el hit del click. */}
      <a
        className={styles.cta}
        href={STORE_URLS[platform]}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onDownload}
      >
        <Icon name="download" size={16} />
        {t("app_banner_cta", "DESCARGAR")}
      </a>
    </div>
  );
};

export default AppDownloadBanner;
